import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Inbox, PhoneCall, CalendarCheck, Home as HomeIcon, PlusCircle, ExternalLink, Tag, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtINR, prettyDate } from "../lib/dates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const LEAD_STATUSES = ["new", "contacted", "closed"];
const STATUS_STYLE = {
  new: "bg-accent/15 text-accent",
  contacted: "bg-primary/15 text-primary",
  closed: "bg-muted text-muted-foreground",
  pending: "bg-accent/15 text-accent",
};

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [onboarding, setOnboarding] = useState([]);
  const [partners, setPartners] = useState([]);
  const [offers, setOffers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [pForm, setPForm] = useState({ name: "", email: "", password: "", company: "" });
  const [pOpen, setPOpen] = useState(false);
  const [pSaving, setPSaving] = useState(false);
  const [oForm, setOForm] = useState({ code: "", title: "", description: "", discount_type: "percent", discount_value: "", audience: "all", start_date: "", end_date: "", min_nights: "1" });
  const [oOpen, setOOpen] = useState(false);
  const [oSaving, setOSaving] = useState(false);

  const loadAll = () => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/onboarding").then(({ data }) => setOnboarding(data)).catch(() => {});
    api.get("/admin/partners").then(({ data }) => setPartners(data)).catch(() => {});
    api.get("/offers").then(({ data }) => setOffers(data)).catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  const changeLeadStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const changeOnboardingStatus = async (id, status) => {
    try {
      await api.patch(`/onboarding/${id}/status`, { status });
      setOnboarding((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const createPartner = async (e) => {
    e.preventDefault();
    if (!pForm.name || !pForm.email || !pForm.password) return toast.error("Fill all fields");
    setPSaving(true);
    try {
      await api.post("/admin/partners", pForm);
      toast.success("Partner created");
      setPForm({ name: "", email: "", password: "", company: "" });
      setPOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPSaving(false);
    }
  };

  const cancelBookingAdmin = async (id) => {
    if (!window.confirm("Cancel this booking and issue a full refund to the customer?")) return;
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: "cancelled", refund_amount: data.refund_amount } : b)));
      toast.success(`Booking cancelled · refund ${data.refund_amount ? "\u20B9" + data.refund_amount.toLocaleString("en-IN") : "processed"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cancel failed");
    }
  };

  const createOffer = async (e) => {
    e.preventDefault();
    if (!oForm.code || !oForm.title || !oForm.discount_value || !oForm.start_date || !oForm.end_date) {
      return toast.error("Fill code, title, value and date range");
    }
    setOSaving(true);
    try {
      await api.post("/offers", {
        ...oForm,
        discount_value: Number(oForm.discount_value),
        min_nights: Number(oForm.min_nights) || 1,
      });
      toast.success("Offer created");
      setOForm({ code: "", title: "", description: "", discount_type: "percent", discount_value: "", audience: "all", start_date: "", end_date: "", min_nights: "1" });
      setOOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setOSaving(false);
    }
  };

  const toggleOffer = async (id, active) => {
    try {
      await api.patch(`/offers/${id}/toggle`, { active });
      setOffers((prev) => prev.map((o) => (o.id === id ? { ...o, active } : o)));
    } catch { toast.error("Update failed"); }
  };

  const deleteOffer = async (id) => {
    try {
      await api.delete(`/offers/${id}`);
      setOffers((prev) => prev.filter((o) => o.id !== id));
      toast.success("Offer deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filteredLeads = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const cards = stats ? [
    { label: "Total Leads", value: stats.total_leads, icon: Inbox },
    { label: "New", value: stats.new_leads, icon: Inbox },
    { label: "Bookings", value: bookings.length, icon: CalendarCheck },
    { label: "Partners", value: partners.length, icon: Users },
  ] : [];

  return (
    <div className="min-h-[80vh] bg-secondary/40" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Admin</p>
            <h1 className="font-heading text-4xl font-light mt-2">Operations Dashboard</h1>
          </div>
          <Button asChild variant="outline" className="rounded-full gap-2" data-testid="admin-inventory-link">
            <Link to="/apukaa-inventory">Manage Availability <ExternalLink className="h-4 w-4" /></Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} data-testid={`stat-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-card border border-border rounded-lg p-6">
                <Icon className="h-6 w-6 text-accent mb-3" strokeWidth={1.5} />
                <div className="font-heading text-4xl font-light">{c.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{c.label}</div>
              </div>
            );
          })}
        </div>

        <Tabs defaultValue="leads" className="mt-12">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="onboarding" data-testid="tab-onboarding">Villa Onboarding</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">Partners</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers">Offers</TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-light">Inquiries</h2>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40" data-testid="admin-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
              <Table data-testid="admin-leads-table">
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Service</TableHead><TableHead>Message</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="admin-no-leads">No leads found.</TableCell></TableRow>
                  ) : filteredLeads.map((l) => (
                    <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`}>
                      <TableCell className="font-medium">{l.name}</TableCell>
                      <TableCell className="text-sm"><div>{l.email}</div><div className="text-muted-foreground">{l.phone}</div></TableCell>
                      <TableCell className="text-sm">{l.service}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{l.message || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Select value={l.status} onValueChange={(v) => changeLeadStatus(l.id, v)}>
                          <SelectTrigger className={`w-32 h-8 text-xs font-semibold border-0 ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`} data-testid={`admin-status-select-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-4">Villa Booking Requests</h2>
            <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
              <Table data-testid="admin-bookings-table">
                <TableHeader>
                  <TableRow><TableHead>Guest</TableHead><TableHead>Villa</TableHead><TableHead>Dates</TableHead><TableHead>Guests</TableHead><TableHead>Total</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground" data-testid="admin-no-bookings">No booking requests yet.</TableCell></TableRow>
                  ) : bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`admin-booking-row-${b.id}`}>
                      <TableCell className="font-medium"><div>{b.name}</div><div className="text-sm text-muted-foreground">{b.email} · {b.phone}</div></TableCell>
                      <TableCell className="text-sm">{b.villa_title}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{prettyDate(b.check_in)} → {prettyDate(b.check_out)}<div className="text-muted-foreground">{b.num_nights} nights</div></TableCell>
                      <TableCell>{b.guests}</TableCell>
                      <TableCell className="font-semibold text-accent">{fmtINR(b.total)}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{(b.payment_status || "unpaid").replace("_", " ")}</Badge></TableCell>
                      <TableCell><Badge variant="secondary" className="capitalize">{b.status}</Badge></TableCell>
                      <TableCell>
                        {b.status !== "cancelled" && (
                          <Button size="sm" variant="ghost" data-testid={`admin-cancel-booking-${b.id}`} onClick={() => cancelBookingAdmin(b.id)} className="text-destructive gap-1">
                            <Trash2 className="h-4 w-4" /> Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ONBOARDING */}
          <TabsContent value="onboarding" className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-4">Villa Onboarding Submissions</h2>
            <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
              <Table data-testid="admin-onboarding-table">
                <TableHeader>
                  <TableRow><TableHead>Owner</TableHead><TableHead>Property</TableHead><TableHead>Location</TableHead><TableHead>Type</TableHead><TableHead>Expected Rate</TableHead><TableHead>Status</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {onboarding.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="admin-no-onboarding">No submissions yet.</TableCell></TableRow>
                  ) : onboarding.map((o) => (
                    <TableRow key={o.id} data-testid={`admin-onboarding-row-${o.id}`}>
                      <TableCell className="font-medium"><div>{o.owner_name}</div><div className="text-sm text-muted-foreground">{o.email} · {o.phone}</div></TableCell>
                      <TableCell className="text-sm">{o.villa_name}</TableCell>
                      <TableCell className="text-sm">{o.location}</TableCell>
                      <TableCell className="text-sm">{o.property_type}</TableCell>
                      <TableCell className="text-sm">{o.expected_rate || "—"}</TableCell>
                      <TableCell>
                        <Select value={o.status} onValueChange={(v) => changeOnboardingStatus(o.id, v)}>
                          <SelectTrigger className={`w-32 h-8 text-xs font-semibold border-0 ${STATUS_STYLE[o.status] || STATUS_STYLE.new}`} data-testid={`admin-onboarding-status-${o.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* PARTNERS */}
          <TabsContent value="partners" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-light">Booking Partners</h2>
              <Dialog open={pOpen} onOpenChange={setPOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full gap-2" data-testid="add-partner-btn"><PlusCircle className="h-4 w-4" /> Add Partner</Button>
                </DialogTrigger>
                <DialogContent data-testid="partner-dialog">
                  <DialogHeader><DialogTitle className="font-heading text-2xl font-light">Create booking partner</DialogTitle></DialogHeader>
                  <form onSubmit={createPartner} className="space-y-4">
                    <div className="space-y-2"><Label>Contact name</Label><Input data-testid="partner-name-input" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Company</Label><Input data-testid="partner-company-input" value={pForm.company} onChange={(e) => setPForm({ ...pForm, company: e.target.value })} placeholder="Partner agency name" /></div>
                    <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="partner-email-input" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Temporary password</Label><Input data-testid="partner-password-input" value={pForm.password} onChange={(e) => setPForm({ ...pForm, password: e.target.value })} placeholder="Min. 6 characters" /></div>
                    <Button type="submit" disabled={pSaving} data-testid="partner-create-submit" className="w-full rounded-full">{pSaving ? "Creating..." : "Create Partner"}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
              <Table data-testid="admin-partners-table">
                <TableHeader><TableRow><TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Email</TableHead><TableHead>Added</TableHead></TableRow></TableHeader>
                <TableBody>
                  {partners.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-12 text-muted-foreground" data-testid="admin-no-partners">No partners yet. Add one to let them manage bookings.</TableCell></TableRow>
                  ) : partners.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-partner-row-${p.id}`}>
                      <TableCell className="font-medium flex items-center gap-2"><HomeIcon className="h-4 w-4 text-accent" /> {p.company || "—"}</TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell className="text-sm">{p.email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* OFFERS */}
          <TabsContent value="offers" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-light">Offers &amp; Discounts</h2>
              <Dialog open={oOpen} onOpenChange={setOOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full gap-2" data-testid="add-offer-btn"><PlusCircle className="h-4 w-4" /> Create Offer</Button>
                </DialogTrigger>
                <DialogContent data-testid="offer-dialog" className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-heading text-2xl font-light">Create offer / discount</DialogTitle></DialogHeader>
                  <form onSubmit={createOffer} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Code</Label><Input data-testid="offer-code" value={oForm.code} onChange={(e) => setOForm({ ...oForm, code: e.target.value.toUpperCase() })} placeholder="MONSOON20" /></div>
                      <div className="space-y-2">
                        <Label>Audience</Label>
                        <Select value={oForm.audience} onValueChange={(v) => setOForm({ ...oForm, audience: v })}>
                          <SelectTrigger data-testid="offer-audience"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="customer">Customers</SelectItem><SelectItem value="partner">Partners</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2"><Label>Title</Label><Input data-testid="offer-title" value={oForm.title} onChange={(e) => setOForm({ ...oForm, title: e.target.value })} placeholder="Monsoon Special" /></div>
                    <div className="space-y-2"><Label>Description</Label><Input data-testid="offer-description" value={oForm.description} onChange={(e) => setOForm({ ...oForm, description: e.target.value })} /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select value={oForm.discount_type} onValueChange={(v) => setOForm({ ...oForm, discount_type: v })}>
                          <SelectTrigger data-testid="offer-type"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><Label>Value</Label><Input type="number" data-testid="offer-value" value={oForm.discount_value} onChange={(e) => setOForm({ ...oForm, discount_value: e.target.value })} /></div>
                      <div className="space-y-2"><Label>Min nights</Label><Input type="number" min="1" data-testid="offer-min-nights" value={oForm.min_nights} onChange={(e) => setOForm({ ...oForm, min_nights: e.target.value })} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Valid from</Label><Input type="date" data-testid="offer-start" value={oForm.start_date} onChange={(e) => setOForm({ ...oForm, start_date: e.target.value })} /></div>
                      <div className="space-y-2"><Label>Valid until</Label><Input type="date" data-testid="offer-end" value={oForm.end_date} onChange={(e) => setOForm({ ...oForm, end_date: e.target.value })} /></div>
                    </div>
                    <Button type="submit" disabled={oSaving} data-testid="offer-create-submit" className="w-full rounded-full">{oSaving ? "Creating..." : "Create Offer"}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
              <Table data-testid="admin-offers-table">
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Title</TableHead><TableHead>Discount</TableHead><TableHead>Audience</TableHead><TableHead>Validity</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {offers.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground" data-testid="admin-no-offers">No offers yet. Create a seasonal discount.</TableCell></TableRow>
                  ) : offers.map((o) => (
                    <TableRow key={o.id} data-testid={`admin-offer-row-${o.id}`}>
                      <TableCell className="font-mono font-semibold flex items-center gap-2"><Tag className="h-4 w-4 text-accent" /> {o.code}</TableCell>
                      <TableCell className="text-sm">{o.title}</TableCell>
                      <TableCell className="text-sm text-accent font-semibold">{o.discount_type === "percent" ? `${o.discount_value}%` : fmtINR(o.discount_value)}</TableCell>
                      <TableCell className="text-sm capitalize">{o.audience}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{prettyDate(o.start_date)} – {prettyDate(o.end_date)}</TableCell>
                      <TableCell><Switch checked={o.active} onCheckedChange={(v) => toggleOffer(o.id, v)} data-testid={`offer-toggle-${o.id}`} /></TableCell>
                      <TableCell><Button size="icon" variant="ghost" data-testid={`offer-delete-${o.id}`} onClick={() => deleteOffer(o.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
